import {
  addGameObject,
  anchoredPos,
  getEntityStateAndProgress,
  getZeroStaticEntity,
  setParent,
  highlightStartedThisFrame,
  highlightStoppedThisFrame,
} from './entity';
import { createAndAddLocalContainer, setContainerStyle } from './container';
import { API_HOST, debugState } from './index';
import { setVisibilityState, setParentByEntityProps } from './simulate';
import { cloneV2, noAutoResize, remapNumber01, v2 } from './helpers';
import {
  Alignment_Horizontal,
  InfoGraphicHotspotData,
  InfoGraphicTextPlacement,
  ObjectType,
  ServerEvent_EntityProps,
  ServerEvent_InfoGraphic,
  Side,
} from './shared/api_shared_types';
import {
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  InfoGraphic,
  MoveMode,
  Shape,
  TextAppearStyle,
  type ImageEnt,
  type InfoGraphicHotspot,
  type TextObject,
  type TextObjectStyle,
} from './types';
import * as DataController from './controllers/data_controller';
import { createImageEnt } from './image_entity';
import { textFade, changeTextObjectText, createAndAddLocalTextObject } from './textobject/textobject';
import { SHAPE_STYLES, TEXT_STYLE } from './constants';
import logger from './controllers/logger';

export function controller(): GameObjectController {
  return {
    getNew: getZeroInfoGraphic,
    sync,
    update,
    postUpdate,
  };
}

export function getZeroInfoGraphic(ent: Entity): InfoGraphic {
  let newInfoGraphic: InfoGraphic = {
    ...ent,
    objectType: ObjectType.INFOGRAPHIC,

    imageUri: '',
    widthEm: 0,
    textWidthEm: 3,

    horTextOffsetEm: 0,
    vertTextOffsetEm: 0,
    showBorder: false,

    textAlign: Alignment_Horizontal.LEFT,
    textPlacement: Side.RIGHT,
    defaultTextStyle: '',

    imageEnt: null,
    tObj: null,

    activeHotspotIndex: -1,

    defaultText: '',
    hotspots: [],
  };

  return newInfoGraphic;
}

export function sync(
  manager: GameManager,
  infoGraphic: InfoGraphic,
  data: ServerEvent_InfoGraphic,
) {
  let entityProps: ServerEvent_EntityProps = {};

  if (data.hasOwnProperty('entityProps')) {
    entityProps = data.entityProps;
  }

  if (data.hasOwnProperty('widthEm')) {
    infoGraphic.widthEm = data.widthEm;
  }

  if (data.hasOwnProperty('textWidthEm')) {
    infoGraphic.textWidthEm = data.textWidthEm;
  }

  if (data.hasOwnProperty('defaultText')) {
    infoGraphic.defaultText = data.defaultText;
  }

  if (data.hasOwnProperty('horTextOffsetEm')) {
    infoGraphic.horTextOffsetEm = data.horTextOffsetEm;
  }

  if (data.hasOwnProperty('vertTextOffsetEm')) {
    infoGraphic.vertTextOffsetEm = data.vertTextOffsetEm;
  }

  if (data.hasOwnProperty('textAlign')) {
    infoGraphic.textAlign = data.textAlign;
  }

  if (data.hasOwnProperty('textPlacement')) {
    infoGraphic.textPlacement = data.textPlacement;
  }

  if (data.hasOwnProperty('defaultTextStyle')) {
    infoGraphic.defaultTextStyle = data.defaultTextStyle;
  }

  if (!infoGraphic.id) {
    addGameObject(manager, infoGraphic);
    initInfoGraphic(manager, infoGraphic, data);

    setParentByEntityProps(manager, infoGraphic, data.sId, data.roomId, entityProps);
  }

  if (data.hasOwnProperty('hotspots')) {
    initHotSpots(infoGraphic, data.hotspots);
  }

  if (data.hasOwnProperty('showBorder')) {
    infoGraphic.showBorder = data.showBorder;
  }

  // setGameObjectyEntityProps(infoGraphic, entityProps);
}

export function initInfoGraphic(
  manager: GameManager,
  infoGraphic: InfoGraphic,
  data: ServerEvent_InfoGraphic,
) {
  infoGraphic.shape = Shape.RECT;
  infoGraphic.allowHighlight = true;
  infoGraphic.passHover = true;
  infoGraphic.passClick = true;
  infoGraphic.influenceKinetic = true;

  setVisibilityState(infoGraphic, VisibilityState.APPEARING);

  const length = API_HOST.length;
  const imageId = data.imageUri;

  if (data.imageUri.substring(0, length) === API_HOST) {
    DataController.cacheImageFromApiAsync(manager.user.accessToken, data.imageUri, imageId);
  } else {
    DataController.cacheImage(data.imageUri, imageId);
  }

  const props: Partial<ImageEnt> = {
    disableAutoSize: true,
    allowHighlight: true,
    passHover: true,
    passClick: true,
    minWidthEm: infoGraphic.widthEm,
    maxWidthEm: infoGraphic.widthEm,
  };
  infoGraphic.imageEnt = createImageEnt(manager, getZeroStaticEntity(), imageId, props);
  setParent(infoGraphic.imageEnt, infoGraphic, v2());

  infoGraphic.tObj = createTextObject(infoGraphic, infoGraphic.defaultText);
  if (infoGraphic.defaultText.length === 0) {
    setVisibilityState(infoGraphic.tObj, VisibilityState.HIDDEN);
  }

  return infoGraphic;
}

function initHotSpots(infoGraphic: InfoGraphic, hotSpotDataList: InfoGraphicHotspotData[]) {
  for (let i = hotSpotDataList.length - 1; i >= 0; i--) {
    const hotspotData = hotSpotDataList[i];

    if (!hotspotData.relativeRadius) {
      hotspotData.relativeRadius = 0;
    }
    if (!hotspotData.relativeWidth) {
      hotspotData.relativeWidth = 0;
    }

    if (!hotspotData.relativeHeight) {
      hotspotData.relativeHeight = 0;
    }

    if (!hotspotData.textPlacement) {
      hotspotData.textPlacement = InfoGraphicTextPlacement.DEFAULT;
    }

    let container = createAndAddLocalContainer(infoGraphic.manager, getZeroStaticEntity());
    container.shape = hotspotData.shape === 'circle' ? Shape.CIRCLE : Shape.RECT;

    setParent(container, infoGraphic.imageEnt);

    container.moveMode = MoveMode.ANCHORED;

    container.anchoredPos = anchoredPos(
      v2(hotspotData.relativePositionX, hotspotData.relativePositionY),
      v2(),
      v2(0.5, 0.5),
    );

    const refWidth = infoGraphic.imageEnt.width;

    container.radius = hotspotData.relativeRadius * refWidth;
    container.width = hotspotData.relativeWidth * refWidth;
    container.height = hotspotData.relativeHeight * refWidth;
    container.allowHighlight = true;
    container.passHover = true;

    setVisibilityState(container, VisibilityState.DEFAULT);

    const newHotspot: InfoGraphicHotspot = {
      ...hotspotData,
      container,
      tObj: null,
    };

    newHotspot.tObj = createTextObject(infoGraphic, hotspotData.text, newHotspot);
    setVisibilityState(newHotspot.tObj, VisibilityState.HIDDEN);

    infoGraphic.hotspots.push(newHotspot);
  }
}

function updateHotspots(infoGraphic: InfoGraphic) {
  const refWidth = infoGraphic.imageEnt.width;

  for (let i = infoGraphic.hotspots.length - 1; i >= 0; i--) {
    let h = infoGraphic.hotspots[i];
    h.container.radius = h.relativeRadius * refWidth;
    h.container.width = h.relativeWidth * refWidth;
    h.container.height = h.relativeHeight * refWidth;
  }
}

function update(obj: InfoGraphic): boolean {
  let { state, progress } = getEntityStateAndProgress(obj);

  if (state === VisibilityState.APPEARING) {
    if (obj.shape === Shape.CIRCLE) {
      obj.scale = progress;
    }
  } else if (state === VisibilityState.DISAPPEARING || state === VisibilityState.HIDING) {
    if (obj.shape === Shape.CIRCLE) {
      obj.scale = 1 - progress;
    } else {
    }
  } else if (state === VisibilityState.DEFAULT) {
    if (obj.scale !== 1) {
      obj.scale = 1;
    }
    if (obj.scale !== 1) {
      obj.scale = 1;
    }
  } else if (state === VisibilityState.HIDDEN) {
    if (obj.scale !== 0) {
      obj.scale = 0;
    }
  }

  let hotSpotStart = -1;
  let hotSpotStop = -1;
  for (let x = 0; x < obj.hotspots.length; x++) {
    if (highlightStartedThisFrame(obj.hotspots[x].container)) {
      hotSpotStart = x;
    } else if (highlightStoppedThisFrame(obj.hotspots[x].container)) {
      hotSpotStop = x;
    }
  }

  if (hotSpotStart >= 0) {
    setHotSpotActive(obj, hotSpotStart);
  } else if (hotSpotStop >= 0) {
    deactivateAllHotspots(obj);
  }

  return false;
}

export function postUpdate(obj: InfoGraphic, childrenChanged: number): boolean {
  if (obj.imageEnt.layoutChanged) {
    updateHotspots(obj);
  }

  return false;
}

function setHotSpotActive(obj: InfoGraphic, hotspotIndex: number) {
  //hide current text/hotspot
  if (obj.activeHotspotIndex >= 0) {
    const h = obj.hotspots[obj.activeHotspotIndex];
    setContainerStyle(h.container, null);
    setVisibilityState(h.tObj, VisibilityState.HIDING);
  } else {
    //hide default text
    setVisibilityState(obj.tObj, VisibilityState.HIDING);
  }

  {
    const h = obj.hotspots[hotspotIndex];
    if (obj.showBorder) {
      setContainerStyle(h.container, SHAPE_STYLES.INFOGRAPHIC_HOTSPOT);
    }
    textFade(obj.tObj, 0);
    setVisibilityState(h.tObj, VisibilityState.APPEARING);
  }

  obj.activeHotspotIndex = hotspotIndex;
}

function deactivateAllHotspots(obj: InfoGraphic) {
  if (obj.activeHotspotIndex >= 0) {
    const h = obj.hotspots[obj.activeHotspotIndex];
    setContainerStyle(h.container, null);
    setVisibilityState(h.tObj, VisibilityState.HIDING);
  }

  if (obj.defaultText.length > 0) {
    setVisibilityState(obj.tObj, VisibilityState.APPEARING);
    textFade(obj.tObj, 0);
  } else {
    setVisibilityState(obj.tObj, VisibilityState.HIDING);
  }

  obj.activeHotspotIndex = -1;
}

function createTextObject(
  infoGraphic: InfoGraphic,
  text: string,
  hotspot: InfoGraphicHotspot = null,
): TextObject {
  let style = hotspot
    ? getTextStyle(hotspot.textStyle)
    : getTextStyle(infoGraphic.defaultTextStyle);

  style.textAlign = infoGraphic.textAlign;

  const tObj = createAndAddLocalTextObject(
    infoGraphic.manager,
    getZeroStaticEntity(),
    text,
    noAutoResize(style),
    infoGraphic.textWidthEm,
    false,
  );
  tObj.allowDrag = false;
  tObj.allowHighlight = false;
  tObj.onlyShowInDefaultState = false;
  tObj.moveMode = MoveMode.ANCHORED;

  if (!hotspot || hotspot.textPlacement === InfoGraphicTextPlacement.DEFAULT) {
    const x = infoGraphic.horTextOffsetEm * 100 || 0;
    const y = infoGraphic.vertTextOffsetEm * 100 || 0;
    switch (infoGraphic.textPlacement) {
      case Side.BELOW:
        {
          tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_DOWN;
          tObj.anchoredPos = anchoredPos(v2(0.5, 1), v2(x, y), v2(0.5, 0));
        }
        break;
      case Side.ABOVE:
        {
          tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_UP;
          tObj.anchoredPos = anchoredPos(v2(0.5, 0), v2(x, y), v2(0.5, 1));
        }
        break;
      case Side.RIGHT:
        {
          tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_RIGHT;
          tObj.anchoredPos = anchoredPos(v2(1, 0.5), v2(x, y), v2(0, 0.5));
        }
        break;
      case Side.LEFT:
        {
          tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_LEFT;
          tObj.anchoredPos = anchoredPos(v2(0, 1), v2(x, y), v2(1, 0));
        }
        break;
      default:
        {
          tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_RIGHT;
          tObj.anchoredPos = anchoredPos(v2(1, 0.5), v2(x, y), v2(0, 0.5));
        }
        break;
    }
    setParent(tObj, infoGraphic.imageEnt);
  } else {
    const x = hotspot.horTextOffsetPx || 0;
    const y = hotspot.vertTextOffsetPx || 0;
    switch (hotspot.textPlacement) {
      case InfoGraphicTextPlacement.BELOW_HOTSPOT:
        {
          tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_DOWN;
          tObj.anchoredPos = anchoredPos(v2(0.5, 1), v2(x, y), v2(0.5, 0));
        }
        break;
      case InfoGraphicTextPlacement.ABOVE_HOTSPOT:
        {
          tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_UP;
          tObj.anchoredPos = anchoredPos(v2(0.5, 0), v2(x, y), v2(0.5, 1));
        }
        break;
      case InfoGraphicTextPlacement.RIGHT_OF_HOTSPOT:
        {
          tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_RIGHT;
          tObj.anchoredPos = anchoredPos(v2(1, 0.5), v2(x, y), v2(0, 0.5));
        }
        break;
      case InfoGraphicTextPlacement.LEFT_OF_HOTSPOT:
        {
          tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_LEFT;
          tObj.anchoredPos = anchoredPos(v2(0, 0.5), v2(x, y), v2(1, 0.5));
        }
        break;
    }
    setParent(tObj, hotspot.container);
  }

  return tObj;
}

function getTextStyle(style: string): TextObjectStyle {
  switch (style) {
    case 'content_default':
      return TEXT_STYLE.CONTENT_DEFAULT;
    case 'content_small':
      return TEXT_STYLE.CONTENT_DEFAULT_SMALL;
    case 'text_block':
      return TEXT_STYLE.TEXT_BLOCK;
    case 'floating_item':
      return TEXT_STYLE.FLOATING;
    case 'floating_item_light':
      return TEXT_STYLE.FLOATING_LIGHT;
    case 'floating_item_small':
      return TEXT_STYLE.FLOATING_SMALL;
    case 'floating_item_green':
      return TEXT_STYLE.FLOATING_GREEN;
    case 'floating_item_red':
      return TEXT_STYLE.FLOATING_RED;
    case 'title':
      TEXT_STYLE.CONTENT_TITLE;
    case 'subtitle':
      TEXT_STYLE.CONTENT_SUBTITLE;
    case 'title_big':
      return TEXT_STYLE.CONTENT_TITLE_BIG;
    case 'question':
      return TEXT_STYLE.FLOATING_QUESTION;
    case 'default':
      return TEXT_STYLE.DEFAULT_NEW;
    case 'default_panel':
      return TEXT_STYLE.DEFAULT_PANEL;
    default:
      return TEXT_STYLE.CONTENT_DEFAULT;
  }
}
